import { useCallback, useState } from "react";
import axios from "axios";
import { DictType } from "@pma/types";
import { ACCESS_TOKEN } from "@pma/utils/constants";
import { toast } from "@hooks/use-toast";
import useTranslator from "@hooks/use-translator";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@components/common/card";
import { Input } from "@components/common/input";
import { Separator } from "@components/common/separator";
import { Button } from "@components/common/button";
import { parseCookies } from "nookies";
import {
  PROJECT_MAP_DELETE_ANNOTATIONS_ENDPOINT,
  PROJECT_MAP_EDIT_ANNOTATIONS_ENDPOINT,
  SERVER_URL,
} from "@pma/utils/api-routes";
import { Marker, MarkerDragEvent, Popup } from "react-map-gl";
import { USERS_PAGE_ROUTE } from "@pma/utils/page-routes";
import { Tooltip, TooltipContent, TooltipTrigger } from "../common/tooltip";
import SimpleImage from "../image-tags/SimpleImage";
import { Link } from "react-router-dom";
import { useAuth } from "@pma/stores/auth-store";
import { cn, getUserFromId } from "@pma/utils/helpers";
import { Pen, Trash, X } from "lucide-react";
import { useMutation } from "@tanstack/react-query";

interface Props {
  annotation: DictType;

  refetch: CallableFunction;
}
const AnnotationPreview = ({ annotation, refetch }: Props) => {
  const { translate, locale } = useTranslator();
  const { authConfig } = useAuth();

  const userId = authConfig?.id;

  const [editing, setEditing] = useState(false);
  const [showAnnotation, setShowAnnotation] = useState(false);

  const [annotationText, setAnnotationText] = useState(
    annotation?.annotation ? annotation?.annotation : "",
  );
  const [_, logEvents] = useState<DictType>({});

  const [marker, setMarker] = useState<DictType>({
    longitude: annotation.lon,
    latitude: annotation.lat,
  });

  const onMarkerDragStart = useCallback((event: MarkerDragEvent) => {
    logEvents((_events) => ({ ..._events, onDragStart: event.lngLat }));
  }, []);

  const onMarkerDrag = useCallback((event: MarkerDragEvent) => {
    logEvents((_events) => ({ ..._events, onDrag: event.lngLat }));

    setMarker({
      longitude: event.lngLat.lng,
      latitude: event.lngLat.lat,
    });
  }, []);

  const onMarkerDragEnd = useCallback((event: MarkerDragEvent) => {
    setMarker({
      longitude: event.lngLat.lng,
      latitude: event.lngLat.lat,
    });
    logEvents((_events) => ({ ..._events, onDragEnd: event.lngLat }));
    return patchAnnotationMutation.mutate();
  }, []);

  const accessToken = parseCookies()[ACCESS_TOKEN];

  const canSave = annotationText !== "";

  const organizationUsers = authConfig?.organizationUsers;

  const responsibleUser = getUserFromId(annotation?.user_id, organizationUsers);

  const patchAnnotation = async () => {
    const response = await axios.post(
      SERVER_URL + PROJECT_MAP_EDIT_ANNOTATIONS_ENDPOINT + `/${annotation?.id}`,

      {
        annotation:
          annotationText !== annotation?.annotation
            ? annotationText
            : annotation?.annotation,
        lon: marker?.longitude,
        lat: marker.latitude,
        user_id: userId,
      },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );

    return response.data;
  };

  const patchAnnotationMutation = useMutation({
    mutationKey: ["add-annotation"],
    mutationFn: patchAnnotation,
    onSuccess: () => {
      toast({
        title: translate("Success"),
        description: translate("Annotation Added"),
        variant: "success",
      });

      refetch();
    },
    onError: (error: DictType) => {
      const errorMessage = error?.response?.data?.message;

      toast({
        title: translate("Error"),
        description: errorMessage || translate("An error occurred"),
        variant: "error",
      });
    },
  });

  const deleteAnnotation = async () => {
    const response = await axios.delete(
      SERVER_URL +
        PROJECT_MAP_DELETE_ANNOTATIONS_ENDPOINT +
        `/${annotation?.id}`,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        data: {
          user_id: userId,
        },
      },
    );

    return response.data;
  };

  const deleteAnnotationMutation = useMutation({
    mutationKey: ["delete-annotation", annotation],
    mutationFn: deleteAnnotation,
    onSuccess: () => {
      toast({
        title: translate("Success"),
        description: translate("Annotation Deleted"),
        variant: "info",
      });

      refetch();
    },
    onError: (error: DictType) => {
      const errorMessage = error?.response?.data?.message;

      toast({
        title: translate("Error"),
        description: errorMessage || translate("An error occurred"),
        variant: "error",
      });
    },
  });

  return (
    <Marker
      latitude={marker?.latitude}
      longitude={marker?.longitude}
      draggable
      onClick={(e) => {
        e.originalEvent.stopPropagation();

        setShowAnnotation((prev) => !prev);
      }}
      onDragStart={onMarkerDragStart}
      onDrag={onMarkerDrag}
      onDragEnd={onMarkerDragEnd}
      style={{ cursor: "pointer" }}
    >
      {showAnnotation && (
        <Popup
          latitude={marker?.latitude}
          longitude={marker?.longitude}
          closeOnClick={false}
        >
          {editing ? (
            <Card className="bg-white shadow-none border-none w-full">
              <CardHeader className="p-0">
                <CardTitle>{translate("Edit annotation")}</CardTitle>{" "}
              </CardHeader>
              <Separator className="my-2" />

              <CardContent className="px-0 flex flex-col gap-2">
                <Input
                  value={annotationText}
                  onChange={(e) => setAnnotationText(e.target.value)}
                  placeholder={translate("Annotation")}
                  className="w-full"
                  required
                />
                <Button
                  size={"sm"}
                  disabled={!canSave}
                  onClick={() => patchAnnotationMutation.mutate()}
                >
                  {translate("Save changes")}
                </Button>
              </CardContent>
              <CardFooter className="flex justify-end items-center my-0 p-0">
                <div className="flex gap-2">
                  <Button
                    size={"sm"}
                    leftSection={<X size={20} />}
                    variant={"outline"}
                    onClick={() => setEditing(false)}
                  >
                    {translate("Close")}
                  </Button>
                </div>
              </CardFooter>
            </Card>
          ) : (
            <Card className="bg-white shadow-none border-none w-full">
              <CardHeader className="p-0">
                <CardTitle>{annotationText}</CardTitle>{" "}
              </CardHeader>
              <Separator className="my-2" />
              <CardContent className="px-0 flex flex-col ">
                <div className="flex">
                  <div className="capitalize font-medium p-1">
                    {translate("Lng")}
                  </div>

                  <div className="text-left p-1">{annotation?.lon}</div>
                </div>
                <div className="flex">
                  <div className="capitalize font-medium p-1">
                    {translate("Lat")}
                  </div>

                  <div className="text-left p-1">{annotation?.lat}</div>
                </div>
              </CardContent>
              <CardFooter className="flex justify-between items-center my-0 p-0">
                <div className="flex gap-2">
                  <Button
                    size={"icon"}
                    className="h-5 w-5"
                    variant={"ghost"}
                    onClick={() => setEditing(true)}
                  >
                    <Pen size={12} />
                  </Button>
                  <Button
                    size={"icon"}
                    className="h-5 w-5 text-destructive"
                    variant={"ghost"}
                    onClick={() => deleteAnnotationMutation.mutate()}
                  >
                    <Trash size={12} />
                  </Button>
                </div>

                <div className="flex gap-2 items-center">
                  <span className="text-slate-500 text-xs">
                    {translate("Added by")}:
                  </span>
                  <Tooltip>
                    <TooltipTrigger>
                      <Link to={USERS_PAGE_ROUTE + `/${annotation?.user_id}`}>
                        <SimpleImage
                          title="User Profile"
                          src={responsibleUser?.user_picture?.url}
                          className="h-5 w-5 rounded-full"
                        />
                      </Link>
                    </TooltipTrigger>
                    <TooltipContent>{responsibleUser?.name}</TooltipContent>
                  </Tooltip>
                </div>
              </CardFooter>
            </Card>
          )}
        </Popup>
      )}

      <img
        onClick={() => setShowAnnotation(true)}
        className={cn("h-5 w-5")}
        src={`${process.env.PUBLIC_URL}/message1.svg`}
      />
    </Marker>
  );
};

export default AnnotationPreview;
